import type { AuthUser, LoginResult } from "@cafe/shared";
import { create } from "zustand";
import { api, configureApi } from "@/shared/api/client";
import { queryClient } from "@/shared/api/query-client";
import { forgetDevice, getDevice } from "@/features/terminal/device";

type Status = "unknown" | "authenticated" | "anonymous";

type AuthState = {
  status: Status;
  /** Access-токен только в памяти (refresh — HttpOnly cookie). */
  accessToken: string | null;
  user: AuthUser | null;
  branchId: string | null;
  /** Сеанс поддержки платформы (impersonation): режим и срок; refresh нет */
  impersonation: { mode: "read_only" | "full"; expiresAt: string } | null;
  /** Экран без входа (кухня, готовность): токен устройства вместо пользователя; user = null */
  screen: ScreenInfo | null;
  setImpersonation: (v: AuthState["impersonation"]) => void;
  setSession: (r: Extract<LoginResult, { status: "ok" }>) => void;
  setBranch: (id: string | null) => void;
  clear: () => void;
};

const BRANCH_KEY = "cafe.branch";
const IMP_KEY = "cafe.impersonation";
export type ScreenInfo = { deviceId: string; branchId: string; type: string; name: string; branchName: string };
type ScreenSession = { accessToken: string; accessTokenExpiresAt: string; device: Omit<ScreenInfo, "branchName">; branch: { name: string } };
type ImpData = { login: Extract<LoginResult, { status: "ok" }>; mode: "read_only" | "full"; expiresAt: string };

/** Начать сеанс поддержки (данные из фрагмента URL): сессия в памяти + sessionStorage вкладки до истечения токена. */
export function startImpersonation(d: ImpData) {
  useAuth.getState().clear();
  useAuth.getState().setSession(d.login);
  useAuth.getState().setImpersonation({ mode: d.mode, expiresAt: d.expiresAt });
  try {
    sessionStorage.setItem(IMP_KEY, JSON.stringify(d));
  } catch {
    /* приватный режим */
  }
}

/** Восстановить сеанс поддержки после перезагрузки вкладки (если не истёк). */
export function restoreImpersonation(): boolean {
  try {
    const d = JSON.parse(sessionStorage.getItem(IMP_KEY) ?? "null") as ImpData | null;
    if (!d || new Date(d.expiresAt) <= new Date()) return false;
    useAuth.getState().setSession(d.login);
    useAuth.getState().setImpersonation({ mode: d.mode, expiresAt: d.expiresAt });
    return true;
  } catch {
    return false;
  }
}
const readBranch = () => {
  try {
    return localStorage.getItem(BRANCH_KEY);
  } catch {
    return null;
  }
};

export const useAuth = create<AuthState>((set) => ({
  status: "unknown",
  accessToken: null,
  user: null,
  branchId: readBranch(),
  impersonation: null,
  screen: null,
  setImpersonation: (impersonation) => set({ impersonation }),
  setSession: (r) =>
    set((s) => {
      // Другой пользователь на том же устройстве — не показываем чужие данные из кэша
      if ((s.user && s.user.id !== r.user.id) || s.screen) queryClient.clear();
      return { status: "authenticated", accessToken: r.accessToken, user: r.user, screen: null };
    }),
  setBranch: (branchId) => {
    try {
      if (branchId) localStorage.setItem(BRANCH_KEY, branchId);
      else localStorage.removeItem(BRANCH_KEY);
    } catch {
      /* приватный режим */
    }
    set({ branchId });
  },
  clear: () => {
    queryClient.clear();
    try {
      sessionStorage.removeItem(IMP_KEY);
    } catch {
      /* приватный режим */
    }
    set({ status: "anonymous", accessToken: null, user: null, impersonation: null, screen: null });
  },
}));

/**
 * Сеанс экрана без входа: deviceToken → короткий токен устройства (только маршруты экрана своего филиала).
 * false — устройство отозвано/неизвестно (забываем его) или экран недоступен (тариф, сеть).
 */
export async function startScreenSession(): Promise<boolean> {
  const d = getDevice();
  if (!d) return false;
  try {
    const r = await api<ScreenSession>("POST", "/devices/screen-session", { body: {}, auth: false, headers: { "X-Device-Token": d.deviceToken } }, false);
    if (useAuth.getState().user) queryClient.clear();
    useAuth.getState().setBranch(r.device.branchId);
    useAuth.setState({ status: "authenticated", accessToken: r.accessToken, user: null, impersonation: null, screen: { ...r.device, branchName: r.branch.name } });
    return true;
  } catch (e) {
    const code = (e as { code?: string }).code;
    if (code === "DEVICE_REVOKED" || code === "DEVICE_REQUIRED") forgetDevice();
    throw e;
  }
}

/** Обновление access по refresh-cookie. Используется при старте и при 401. */
export async function refreshSession(): Promise<boolean> {
  // В сеансе поддержки refresh нет: cookie реального пользователя в этом браузере не должна подменить сеанс
  if (useAuth.getState().impersonation) return false;
  // Экран без входа обновляет токен по deviceToken
  if (useAuth.getState().screen) return startScreenSession().catch(() => false);
  try {
    const r = await api<Extract<LoginResult, { status: "ok" }>>("POST", "/auth/refresh", { body: {}, auth: false }, false);
    useAuth.getState().setSession(r);
    return true;
  } catch {
    return false;
  }
}

configureApi({
  getToken: () => useAuth.getState().accessToken,
  getBranchId: () => useAuth.getState().branchId,
  refresh: refreshSession,
  onUnauthorized: () => useAuth.getState().clear(),
});
